/** Phase 4.1.0 — Autopilot run detail.
 *
 * Shows: header (mode/version/status + AIRunBadge), observations card,
 * candidate actions card (collapsible — shows rule verdicts), selected
 * actions card (proposed change JSON + state badge + "Open approval"
 * button for queued-for-approval items), skipped actions card.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AuditHistoryLink from "../../components/app/AuditHistoryLink";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

const STATE_TONE = {
  pending: "secondary", queued_for_approval: "secondary",
  executing: "default", executed: "default",
  rejected: "destructive", failed: "destructive",
  rolled_back: "outline",
};

function StateBadge({ state }) {
  return (
    <Badge variant={STATE_TONE[state] || "outline"} data-testid={`action-state-${state}`}>
      {state}
    </Badge>
  );
}

function ActionTypeBadge({ at }) {
  const t = useT();
  return (
    <Badge variant="outline" className="font-mono text-[11px]"
      data-testid={`action-type-${at}`}>
      {t(`autopilot.action_types.${at}`) || at}
    </Badge>
  );
}

function RuleVerdictList({ rules }) {
  const t = useT();
  if (!rules || rules.length === 0) return null;
  return (
    <ul className="space-y-1 text-xs" data-testid="rule-verdicts">
      {rules.map((r, i) => (
        <li key={i} className="flex items-center gap-2"
          data-testid={`rule-verdict-${r.rule}`}>
          <Badge variant={r.allowed ? "outline" : "destructive"} className="font-mono text-[10px]">
            {r.allowed ? "OK" : "BLOCK"}
          </Badge>
          <span className="font-mono">{t(`autopilot.rule_verdicts.${r.rule}`) || r.rule}</span>
          {r.reason && <span className="text-muted-foreground">— {r.reason}</span>}
        </li>
      ))}
    </ul>
  );
}

function ActionCard({ a }) {
  const t = useT();
  const nav = useNavigate();
  return (
    <div data-testid={`action-card-${a.id}`}
      className="border rounded-md p-3 space-y-2 bg-card/50">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ActionTypeBadge at={a.action_type} />
          <StateBadge state={a.state} />
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="font-mono text-[10px]"
            data-testid={`action-confidence-${a.id}`}>
            {(a.confidence || 0).toFixed(2)}
          </Badge>
        </div>
      </div>
      {a.rationale && (
        <p className="text-sm text-muted-foreground" data-testid={`action-rationale-${a.id}`}>
          {a.rationale}
        </p>
      )}
      {a.target_resource_id && (
        <p className="text-xs font-mono">
          <span className="text-muted-foreground">{t("autopilot.runs_detail.target")}:</span>{" "}
          {a.target_resource_type}/{(a.target_resource_id || "").slice(0, 12)}
        </p>
      )}
      {a.proposed_change && Object.keys(a.proposed_change).length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            {t("autopilot.runs_detail.proposed_change")}
          </summary>
          <pre className="mt-2 rounded bg-muted/30 p-2 overflow-auto text-[11px]"
            data-testid={`action-change-${a.id}`}>
            {JSON.stringify(a.proposed_change, null, 2)}
          </pre>
        </details>
      )}
      {(a.blocking_rules || []).length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            {t("autopilot.runs_detail.rule_verdicts")}
          </summary>
          <div className="mt-2"><RuleVerdictList rules={a.blocking_rules} /></div>
        </details>
      )}
      {a.execution_result && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            {t("autopilot.runs_detail.execution_result")}
          </summary>
          <pre className="mt-2 rounded bg-muted/30 p-2 overflow-auto text-[11px]"
            data-testid={`action-result-${a.id}`}>
            {JSON.stringify(a.execution_result, null, 2)}
          </pre>
        </details>
      )}
      <div className="flex justify-end gap-2 pt-1">
        {a.state === "queued_for_approval" && a.approval_request_id && (
          <Button size="sm" variant="outline"
            data-testid={`action-open-approval-${a.id}`}
            onClick={() => nav(`/app/approvals?id=${a.approval_request_id}`)}>
            {t("autopilot.runs_detail.open_approval")}
          </Button>
        )}
        {a.state === "pending" && (
          <Button size="sm" variant="outline" disabled
            data-testid={`action-convert-disabled-${a.id}`}
            title={t("autopilot.runs_detail.convert_disabled_tooltip")}>
            {t("autopilot.runs_detail.convert_to_approval")}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AutopilotRunDetail() {
  const { id } = useParams();
  const t = useT();
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/autopilot/runs/${id}`).then((r) => {
      if (alive) { setRun(r.data); setLoading(false); }
    }).catch((e) => {
      if (alive) {
        setError(e?.response?.status === 404
          ? t("autopilot.runs_detail.not_found")
          : t("autopilot.errors.load_failed"));
        setLoading(false);
      }
    });
    return () => { alive = false; };
  }, [id, t]);

  if (loading) return <p className="text-sm text-muted-foreground" data-testid="run-loading">{t("common.loading")}</p>;
  if (error || !run) return (
    <div data-testid="run-detail-error" className="text-sm">
      <p className="text-rose-600 mb-2">{error || t("autopilot.runs_detail.not_found")}</p>
      <Link to="/app/autopilot" className="text-sm underline">{t("autopilot.runs_detail.back")}</Link>
    </div>
  );

  return (
    <div data-testid="run-detail" className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("autopilot.runs_detail.title")}</h1>
          <p className="text-xs text-muted-foreground font-mono mt-1 flex items-center gap-3">
            <span>run/{(run.id || "").slice(0, 12)} · policy v{run.policy_version} · {run.triggered_by}</span>
            <AuditHistoryLink kind="autopilot_run" id={run.id}
                              testid="autopilot-audit-history-link" />
          </p>
        </div>
        <Link to="/app/autopilot" className="text-xs underline text-muted-foreground"
          data-testid="run-detail-back">
          {t("autopilot.runs_detail.back")}
        </Link>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t("autopilot.runs_detail.header")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono"
            data-testid="run-header-grid">
            <div><span className="text-muted-foreground">status:</span> {run.status}</div>
            <div><span className="text-muted-foreground">started:</span> {run.started_at ? new Date(run.started_at).toLocaleString() : "—"}</div>
            <div><span className="text-muted-foreground">ended:</span> {run.ended_at ? new Date(run.ended_at).toLocaleString() : "—"}</div>
            <div><span className="text-muted-foreground">latency:</span> {run.latency_ms ? `${(run.latency_ms / 1000).toFixed(1)}s` : "—"}</div>
            <div><span className="text-muted-foreground">cost:</span> ${typeof run.cost_usd === "number" ? run.cost_usd.toFixed(4) : "0.0000"}</div>
            <div><span className="text-muted-foreground">selected:</span> {(run.selected_action_ids || []).length}</div>
            <div><span className="text-muted-foreground">skipped:</span> {(run.skipped_actions || []).length}</div>
            <div data-testid="run-optimization-agent-link">
              <span className="text-muted-foreground">agent:</span>{" "}
              {run.optimization_agent_run_id
                ? <span className="text-[10px]">{(run.optimization_agent_run_id || "").slice(0, 8)}</span>
                : <span className="text-muted-foreground">{t("autopilot.runs_detail.deterministic")}</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="run-observations-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("autopilot.runs_detail.observations")}</CardTitle></CardHeader>
        <CardContent>
          {(run.observations || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">{t("autopilot.runs_detail.no_observations")}</p>
          ) : (
            <ul className="space-y-1 text-xs">
              {(run.observations || []).map((o, i) => (
                <li key={i} data-testid={`run-observation-${i}`}>
                  <Badge variant="outline" className="font-mono text-[10px] mr-2">{o.kind}</Badge>
                  <span className="font-mono text-[11px]">
                    {Object.entries(o).filter(([k]) => k !== "kind").map(([k, v]) => `${k}=${v}`).join(", ")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card data-testid="run-selected-actions-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm">{t("autopilot.runs_detail.selected_actions")}</CardTitle>
          <Badge variant="outline" className="font-mono text-[10px]">
            {(run.actions || []).length}
          </Badge>
        </CardHeader>
        <CardContent>
          {(run.actions || []).length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="run-no-selected">
              {t("autopilot.runs_detail.no_selected")}
            </p>
          ) : (
            <div className="space-y-3">
              {(run.actions || []).map((a) => <ActionCard key={a.id} a={a} />)}
            </div>
          )}
        </CardContent>
      </Card>

      {(run.candidate_actions || []).length > 0 && (
        <Card data-testid="run-candidates-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">{t("autopilot.runs_detail.candidates")}</CardTitle></CardHeader>
          <CardContent>
            <details>
              <summary className="cursor-pointer text-xs text-muted-foreground">
                {t("autopilot.runs_detail.candidates_show", { n: (run.candidate_actions || []).length })}
              </summary>
              <ul className="mt-2 space-y-1 text-xs">
                {(run.candidate_actions || []).map((c, i) => (
                  <li key={i} className="flex items-center gap-2" data-testid={`run-candidate-${i}`}>
                    <ActionTypeBadge at={c.action_type} />
                    <span className="text-muted-foreground">{c.rationale}</span>
                  </li>
                ))}
              </ul>
            </details>
          </CardContent>
        </Card>
      )}

      {(run.skipped_actions || []).length > 0 && (
        <Card data-testid="run-skipped-actions-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">{t("autopilot.runs_detail.skipped_actions")}</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-2 text-xs">
              {(run.skipped_actions || []).map((s, i) => (
                <li key={i} data-testid={`run-skipped-${i}`}
                  className="border rounded-md p-2 bg-muted/20">
                  {s.candidate?.action_type && <ActionTypeBadge at={s.candidate.action_type} />}
                  {s.reason && <span className="ml-2 text-muted-foreground">{s.reason}</span>}
                  {(s.blocking_rules || []).length > 0 && (
                    <RuleVerdictList rules={s.blocking_rules.map((b) => ({
                      ...b, allowed: false,
                    }))} />
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
